/**
 * Checklista akt osobowych (spec 02 §182) — konfiguracja, nie dane demo.
 *
 * Spec wymienia osiem pozycji, ale ósma („Aneks — przedłużenie umowy") jest
 * kontekstowa: dotyczy wyłącznie kończącej się umowy i u nas jest flagą
 * pochodną liczoną z `contract_end_at` (spec §344 mówi to wprost). W aktach
 * zostaje siedem stałych pozycji.
 *
 * Pozycje `auto` odhacza system, gdy proces zatrudnienia dostarcza dowód.
 * Pozycje `manual` to dokumenty papierowe — odhacza je dział HR ręcznie, bo
 * nikt inny o nich nie wie. Podział jest istotny: pozycja bez żadnej ścieżki
 * odhaczenia byłaby wieczną „brakującą", której użytkownik nie może domknąć.
 */
import { isPit2Applicable } from './contracts'
import { isEmploymentContract, type ContractType } from './enums'
import { registrationFormFor, type InsuranceScope } from './registrations'
/* Kolejność z makiety (spec 02 §182) — w niej pozycje wracają z API (grupa 127). */
export const EMPLOYEE_FILE_CHECKLIST_ITEMS = [
  { key: 'kwestionariusz_osobowy', source: 'auto' },
  { key: 'umowa_o_prace', source: 'auto' },
  { key: 'badania_lekarskie', source: 'auto' },
  { key: 'szkolenie_bhp', source: 'auto' },
  { key: 'oswiadczenie_pit2', source: 'manual' },
  { key: 'zgloszenie_zus_zua', source: 'manual' },
  { key: 'rachunek_bankowy', source: 'auto' },
] as const satisfies readonly { key: string; source: 'auto' | 'manual' }[]

export type FileChecklistItemKey = (typeof EMPLOYEE_FILE_CHECKLIST_ITEMS)[number]['key']

export const FILE_CHECKLIST_KEYS: readonly FileChecklistItemKey[] =
  EMPLOYEE_FILE_CHECKLIST_ITEMS.map((item) => item.key)

/** Pozycje, które dział HR odhacza ręcznie — tylko te przyjmuje endpoint zapisu. */
export const MANUAL_FILE_CHECKLIST_KEYS: readonly FileChecklistItemKey[] =
  EMPLOYEE_FILE_CHECKLIST_ITEMS.filter((item) => item.source === 'manual').map((item) => item.key)

export function isFileChecklistKey(value: unknown): value is FileChecklistItemKey {
  return typeof value === 'string' && (FILE_CHECKLIST_KEYS as readonly string[]).includes(value)
}

export function isManualFileChecklistKey(value: unknown): value is FileChecklistItemKey {
  return (
    typeof value === 'string' && (MANUAL_FILE_CHECKLIST_KEYS as readonly string[]).includes(value)
  )
}

/** Fakty z akt, od których poza rodzajem umowy zależy zgłoszenie do ZUS. */
export interface FileChecklistFacts {
  /** Student do 26 lat na zleceniu — bez tytułu do ubezpieczeń (art. 6 ust. 4 u.s.u.s.). */
  readonly mandateStudentExempt?: boolean | null
  /** Zakres ubezpieczenia z profilu płacowego — „tylko zdrowotne" to formularz ZZA. */
  readonly insuranceScope?: InsuranceScope | null
}

/**
 * Pozycje właściwe dla historii umów osoby (Fala 6, grupy 34 i 119) — SUMA po
 * wszystkich rodzajach umowy z przyjętych wersji warunków: zmiana umowy
 * o pracę na B2B aneksem nie zdejmuje ZUS ZUA, bo zgłoszenie było obowiązkiem
 * za okres umowy o pracę. Zgłoszenie idzie za tą samą regułą co rejestr
 * zgłoszeń (`registrationFormFor`), więc student na zleceniu go nie ma.
 * Pusta historia albo nieznany rodzaj — komplet. Badania i BHP zostają przy
 * każdej umowie (art. 304 k.p., ADR-0047).
 */
export function fileChecklistKeysFor(
  contractTypes: readonly (ContractType | null)[],
  facts: FileChecklistFacts = {},
): readonly FileChecklistItemKey[] {
  if (contractTypes.length === 0 || contractTypes.some((type) => type === null)) return FILE_CHECKLIST_KEYS
  const types = contractTypes as readonly ContractType[]
  return FILE_CHECKLIST_KEYS.filter((key) => {
    if (key === 'zgloszenie_zus_zua') {
      return types.some(
        (contractType) =>
          registrationFormFor({ contractType, mandateStudentExempt: facts.mandateStudentExempt ?? false }) !== null,
      )
    }
    if (key === 'oswiadczenie_pit2') return types.some(isPit2Applicable)
    return true
  })
}

/**
 * Nazwa pozycji według bieżącej umowy osoby: zleceniobiorca nie ma „umowy
 * o pracę", a zlecenie „tylko zdrowotne" zgłasza się formularzem ZZA, nie ZUA
 * (ta sama reguła co rejestr zgłoszeń). Klucz pozycji w aktach się nie zmienia.
 */
export function fileChecklistLabelKey(
  key: FileChecklistItemKey,
  contractType: ContractType | null,
  facts: FileChecklistFacts = {},
): string {
  if (key === 'zgloszenie_zus_zua' && contractType !== null) {
    const form = registrationFormFor({
      contractType,
      mandateStudentExempt: facts.mandateStudentExempt ?? false,
      insuranceScope: facts.insuranceScope ?? null,
    })
    return form === 'zza' ? 'zgloszenie_zus_zza' : key
  }
  if (key !== 'umowa_o_prace' || contractType === null || isEmploymentContract(contractType)) return key
  if (contractType === 'zlecenie') return 'umowa_zlecenia'
  if (contractType === 'dzielo') return 'umowa_o_dzielo'
  return 'kontrakt_b2b'
}

/** Pozycje w kolejności z makiety (spec 02 §182, grupa 127). */
export function sortFileChecklist<T extends { itemKey: string }>(items: readonly T[]): T[] {
  const rank = (key: string): number => {
    const index = (FILE_CHECKLIST_KEYS as readonly string[]).indexOf(key)
    return index === -1 ? FILE_CHECKLIST_KEYS.length : index
  }
  return items.toSorted((left, right) => rank(left.itemKey) - rank(right.itemKey))
}

/** Kompletność akt w procentach — `done/total` zaokrąglone (spec 02 §182). */
export function fileChecklistPercent(done: number, total: number): number {
  if (total <= 0) return 0
  return Math.round((done / total) * 100)
}
